var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/**
 * @desc scale
 */
import Scale from './scale';
import { niceTicks } from './helper';
import { log10, inRange } from '../utils/math';
import { templateRich } from '../utils/template';
import * as lodash from '../utils/lodash';
import { isRichFormat } from '../rich';
/**
 * linear scale
 */
var LinearScale = /** @class */ (function (_super) {
    __extends(LinearScale, _super);
    function LinearScale(min, max, start, end) {
        var _this = _super.call(this) || this;
        _this.type = 'linear';
        _this.ticks = [];
        _this._hasBreaks = false;
        _this.min = min;
        _this.max = max;
        _this.start = start;
        _this.end = end;
        _this.ticks = [min, max];
        return _this;
    }
    LinearScale.prototype.setBreaks = function (breaks) {
        var domain = [this.min, this.max];
        var insideBreaks = breaks.filter(function (breakItem) {
            var range = breakItem.range;
            return inRange(range[0], domain) && inRange(range[1], domain);
        });
        this._hasBreaks = insideBreaks.length > 0;
        this.ticks = this.ticks.filter(function (tick) {
            return !insideBreaks.some(function (_a) {
                var range = _a.range;
                return tick > range[0] && tick < range[1];
            });
        });
        var breakSum = lodash.sum(insideBreaks.map(function (item) { return item.range[1] - item.range[0]; }));
        var breakTotalSize = lodash.sum(insideBreaks.map(function (item) { return item.breakSize || 0; }));
        var unitPixel = (this.end - this.start) / (this.max - this.min - breakSum + breakTotalSize);
        var brokenScales = [];
        var breakPositions = [];
        for (var i = 0; i <= insideBreaks.length; i++) {
            var curBreak = insideBreaks[i];
            var prevBreak = insideBreaks[i - 1];
            var prevScale = brokenScales[i - 1];
            var min = i === 0 ? this.min : prevBreak.range[1] - prevBreak.breakSize;
            var max = i === insideBreaks.length ? this.max : curBreak.range[0];
            var start = i === 0 ? this.start : prevScale.end;
            var end = start + (max - min) * unitPixel;
            brokenScales.push(new LinearScale(min, max, start, end));
            if (i < insideBreaks.length) {
                var range = curBreak.range, breakSize = curBreak.breakSize;
                breakPositions.push({
                    range: range,
                    position: end,
                    breakSize: breakSize,
                });
            }
        }
        this._brokenScales = brokenScales;
        this._breakPositions = breakPositions;
    };
    LinearScale.prototype.getBreaks = function () {
        return this._breakPositions || [];
    };
    LinearScale.prototype.getBrokenScales = function () {
        return this._brokenScales;
    };
    LinearScale.prototype.clone = function () {
        return new LinearScale(this.min, this.max, this.start, this.end);
    };
    LinearScale.prototype.niceTicks = function (tickCount, intervalConf) {
        if (intervalConf === void 0) { intervalConf = {}; }
        var ticks = niceTicks(this.min, this.max, tickCount, intervalConf);
        this._forceInterval = intervalConf.interval;
        this.ticks = ticks;
        this.min = ticks[0];
        this.max = ticks[ticks.length - 1];
    };
    LinearScale.prototype.scale = function (value) {
        if (this.min === this.max) {
            return (this.start + this.end) / 2;
        }
        var parseValue = lodash.toNumber(value);
        if (this._hasBreaks) {
            for (var i = 0; i < this._brokenScales.length; i++) {
                var _a = this._brokenScales[i], min = _a.min, max = _a.max;
                if (parseValue >= min && parseValue <= max) {
                    return this._brokenScales[i].scale(value);
                }
            }
            for (var i = 0; i < this._breakPositions.length; i++) {
                var _b = this._breakPositions[i], _c = _b.range, min = _c[0], max = _c[1], position = _b.position;
                if (parseValue > min && parseValue < max) {
                    return position;
                }
            }
        }
        return this.start + ((this.end - this.start) * (parseValue - this.min)) / (this.max - this.min);
    };
    LinearScale.prototype.invert = function (position) {
        return this.min + ((this.max - this.min) * (position - this.start)) / (this.end - this.start);
    };
    LinearScale.prototype.getTicks = function () {
        return this.ticks;
    };
    LinearScale.prototype.getLabel = function (value) {
        var ticks = this.ticks;
        var index = ticks.indexOf(value);
        var isFirst = index === 0;
        var isLast = index === ticks.length - 1;
        var interval = (ticks[ticks.length - 1] - ticks[0]) / (ticks.length - 1);
        var precision = Math.floor(log10(Math.abs(interval)));
        var out;
        if (!isNaN(precision) && !lodash.isNumber(this._forceInterval)) {
            if (precision >= 0) {
                out = value.toFixed(2);
            }
            else {
                out = value.toFixed((precision - 2) * -1);
            }
            if (out.indexOf('.') !== -1) {
                out = out.replace(/0+$/, '').replace(/\.$/, '');
            }
        }
        else {
            out = value.toString();
        }
        var info = { value: parseFloat(out), index: index, isFirst: isFirst, isLast: isLast };
        if (lodash.isString(this.formatter) || isRichFormat(this.formatter)) {
            return templateRich(this.formatter, info);
        }
        if (lodash.isFunction(this.formatter)) {
            return this.formatter(parseFloat(out), index, {
                isFirst: isFirst,
                isLast: isLast,
                ticks: ticks,
            });
        }
        return out;
    };
    LinearScale.prototype.getValueDomain = function () {
        return [this.max, this.min];
    };
    LinearScale.prototype.getPositionRange = function () {
        return [this.end, this.start];
    };
    return LinearScale;
}(Scale));
export default LinearScale;
